import { useEffect, useCallback } from 'react';
import { useTracking } from 'react-tracking';

import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { Box, Grid } from '@simplywallst/ui-core';

import {
  type NoteFragmentType,
  useCompanyNotes,
} from '@/components/Note/hooks/useCompanyNotes';
import { useCompanyNoteModal } from '@/components/Note/components/CompanyNoteModalContext';
import { Note } from '@/components/Note/components/Note';

import { EmptyState } from './components/EmptyState';
import { AddNoteButton } from './styled';

interface Props {
  companyId?: string;
  uniqueSymbol?: string;
  /**
   * If the parent container has a fixed height specify isFloatingButton
   * which make it positioned relative to the parent as it will be floating at the bottom over
   * individual notes that are scrollable.
   * If the parent container has an auto height, leave this prop to false/undefined. In which case the button
   * will be positioned after all notes, not overlapping any notes.
   */
  isFloatingButton?: boolean;
}

export const Notes = ({ companyId, uniqueSymbol, isFloatingButton }: Props) => {
  const { openNoteModal, modalState } = useCompanyNoteModal();

  const canCreateNote = !!companyId && !!uniqueSymbol;
  const { data: notes } = useCompanyNotes(companyId);
  const hasNotes = notes && notes.length > 0;

  useEffect(() => {
    if (canCreateNote && modalState === 'adding') {
      openNoteModal({ companyId, uniqueSymbol });
    }
  }, [companyId, uniqueSymbol, modalState]);

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'Note',
    companyId,
    uniqueSymbol,
  });

  const handleAddNote = useCallback(() => {
    if (!canCreateNote) {
      return;
    }
    trackEvent({
      action: 'click',
      modifier: 'add-note',
    });
    openNoteModal({ companyId, uniqueSymbol });
  }, [companyId, uniqueSymbol]);

  const handleEditNote = useCallback(
    (note: NoteFragmentType) => {
      if (!canCreateNote) {
        return;
      }
      openNoteModal({ note, companyId, uniqueSymbol });
    },
    [companyId, uniqueSymbol]
  );

  return (
    <Track>
      <div data-cy-id="notes">
        {hasNotes ? (
          <Box
            {...(isFloatingButton
              ? {}
              : { position: 'relative', paddingBottom: 'x8spacePx' })}
          >
            <Grid gap="8px">
              {notes.map((n) => (
                <Note key={`note-${n.id}`} note={n} editNote={handleEditNote} />
              ))}
            </Grid>
            <AddNoteButton styleType="louder" onClick={handleAddNote}>
              <PlusSolid height={16} width={16} />
              &nbsp; Add Note
            </AddNoteButton>
          </Box>
        ) : (
          <EmptyState
            handleAddNote={handleAddNote}
            canCreateNote={canCreateNote}
          />
        )}
      </div>
    </Track>
  );
};
